import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, ListTodo, Pill, Utensils, Droplet, Activity, Sunrise, Sun, Sunset, Moon } from 'lucide-react';

export interface ShiftTaskPreviewItem {
  id: string;
  name: string;
  task_type: string;
  filtered_times: string[];
}

const INITIAL_VISIBLE = 6;

type BlockKey = 'dawn' | 'morning' | 'afternoon' | 'night';

const BLOCK_ORDER: BlockKey[] = ['morning', 'afternoon', 'night', 'dawn'];

function blockOf(time: string): BlockKey {
  const hour = parseInt(time.substring(0, 2), 10);
  if (hour < 5) return 'dawn';
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'night';
}

const BLOCK_ICONS: Record<BlockKey, typeof Sun> = {
  morning: Sunrise,
  afternoon: Sun,
  night: Sunset,
  dawn: Moon,
};

function taskIcon(taskType: string) {
  switch (taskType) {
    case 'fixed_medication':
      return <Pill className="w-4 h-4 text-primary shrink-0" />;
    case 'food':
      return <Utensils className="w-4 h-4 text-warning shrink-0" />;
    case 'hygiene':
      return <Droplet className="w-4 h-4 text-info shrink-0" />;
    case 'vital_sign':
      return <Activity className="w-4 h-4 text-info shrink-0" />;
    default:
      return <ListTodo className="w-4 h-4 text-muted-foreground shrink-0" />;
  }
}

const CATEGORY_KEYS: { type: string; labelKey: string }[] = [
  { type: 'fixed_medication', labelKey: 'shiftTasksPreview.categoryMedication' },
  { type: 'food', labelKey: 'shiftTasksPreview.categoryFood' },
  { type: 'hygiene', labelKey: 'shiftTasksPreview.categoryHygiene' },
  { type: 'vital_sign', labelKey: 'shiftTasksPreview.categoryVitals' },
];

interface ShiftTasksPreviewProps {
  tasks: ShiftTaskPreviewItem[];
  totalTaskInstances: number;
}

export function ShiftTasksPreview({ tasks, totalTaskInstances }: ShiftTasksPreviewProps) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    tasks.forEach(task => {
      const key = CATEGORY_KEYS.some(c => c.type === task.task_type) ? task.task_type : 'other';
      counts[key] = (counts[key] || 0) + task.filtered_times.length;
    });
    return counts;
  }, [tasks]);

  const visibleTasks = showAll ? tasks : tasks.slice(0, INITIAL_VISIBLE);

  const groups = useMemo(() => {
    const map = new Map<BlockKey, ShiftTaskPreviewItem[]>();
    visibleTasks.forEach(task => {
      const key = blockOf(task.filtered_times[0] || '00:00');
      map.set(key, [...(map.get(key) || []), task]);
    });
    return BLOCK_ORDER.filter(k => map.has(k)).map(k => ({ key: k, tasks: map.get(k)! }));
  }, [visibleTasks]);

  return (
    <div className="space-y-3">
      {/* Summary chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="secondary" className="text-xs font-semibold">
          {totalTaskInstances} {t('shiftTemplates.taskInstances')}
        </Badge>
        {CATEGORY_KEYS.map(c =>
          categoryCounts[c.type] ? (
            <Badge key={c.type} variant="outline" className="text-xs bg-background font-normal gap-1">
              {taskIcon(c.type)}
              {categoryCounts[c.type]} {t(c.labelKey)}
            </Badge>
          ) : null
        )}
        {categoryCounts.other ? (
          <Badge variant="outline" className="text-xs bg-background font-normal gap-1">
            {taskIcon('other')}
            {categoryCounts.other} {t('shiftTasksPreview.categoryOther')}
          </Badge>
        ) : null}
      </div>

      {tasks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-4 text-center">
          <p className="text-sm text-muted-foreground">{t('shiftTasksPreview.emptyState')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(group => {
            const BlockIcon = BLOCK_ICONS[group.key];
            return (
              <div key={group.key} className="space-y-1.5">
                <div className="flex items-center gap-1.5 px-0.5">
                  <BlockIcon className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(`shiftTasksPreview.block.${group.key}`)}
                  </span>
                  <span className="text-xs text-muted-foreground">· {group.tasks.length}</span>
                </div>
                <div className="space-y-1.5">
                  {group.tasks.map(task => (
                    <div
                      key={task.id}
                      className="flex items-start justify-between gap-2 text-sm bg-card rounded-md px-3 py-2.5 shadow-sm border border-border/50"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {taskIcon(task.task_type)}
                        <span className="truncate font-medium">{task.name}</span>
                      </div>
                      <div className="flex flex-wrap justify-end gap-1 shrink-0 max-w-[50%]">
                        {task.filtered_times.map(time => (
                          <span
                            key={time}
                            className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded whitespace-nowrap"
                          >
                            {time.substring(0, 5)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {tasks.length > INITIAL_VISIBLE && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-primary hover:bg-primary/5"
              onClick={() => setShowAll(v => !v)}
            >
              {showAll ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1.5" />
                  {t('shiftTasksPreview.showLess')}
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1.5" />
                  {t('shiftTasksPreview.showAll', { count: tasks.length })}
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
